import { ChevronLeft } from 'lucide-react';
import React, { useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle, css } from 'styled-components';

/* ======================================================
   THEME (SME Cap Advisory – light)
====================================================== */

const theme = {
  bg: '#e8eaed',
  bgAlt: '#ffffff',
  card: 'rgba(255,255,255,0.9)',
  text: '#111827',
  subText: '#4b5563',
  accent: '#000000',
  border: 'rgba(0,0,0,0.08)',
};

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    background: ${theme.bg};
    color: ${theme.text};
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  }
`;

/* ======================================================
   PAGE
====================================================== */

const TermsPage = () => {
  useEffect(() => window.scrollTo(0, 0), []);
  const path = window.location.pathname;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />

      <Page>
        {/* --- NAV --- */}
        <Nav>
          <NavInner>
            <NavLeft onClick={() => (window.location.href = '/')}>
              <ChevronLeft size={16} />
              Back to SME Cap Advisory
            </NavLeft>

            <NavLinks>
              <NavLink href="/privacy" $active={path === '/privacy'}>
                Privacy
              </NavLink>
              <NavLink href="/terms" $active={path === '/terms'}>
                Terms
              </NavLink>
            </NavLinks>
          </NavInner>
        </Nav>

        {/* --- HERO --- */}
        <Hero>
          <HeroInner>
            <Eyebrow>SME Cap Advisory</Eyebrow>
            <HeroTitle>Terms of service</HeroTitle>
            <HeroSub>
              Legal terms governing your access to and use of
              smecapadvisory.com and related advisory services.
            </HeroSub>
          </HeroInner>
        </Hero>

        {/* --- CONTENT --- */}
        <Section>
          <Inner>
            <Card>
              <SectionTitle>
                1. User acknowledgment and acceptance of terms
              </SectionTitle>

              <Paragraph>
                SME Cap Advisory (“SME Cap Advisory”, “we”, “us”, or “our”)
                provides the smecapadvisory.com website and related services
                (collectively, the “site”) subject to your compliance with
                these Terms of Service.
              </Paragraph>

              <Paragraph>
                By accessing or using this site, you agree to be bound by
                these Terms. If you do not agree, you must discontinue use
                of the site immediately.
              </Paragraph>

              <Paragraph>
                These Terms are effective as of <strong>1 November 2024</strong>.
                We reserve the right to modify these Terms at any time.
                Continued use of the site constitutes acceptance of any
                updated Terms.
              </Paragraph>

              <Divider />

              <SectionTitle>2. Description of services</SectionTitle>

              <Paragraph>
                SME Cap Advisory provides strategic and advisory services,
                including (but not limited to) transaction advisory,
                financial analysis, capital strategy, business preparation
                for sale, and related consulting services.
              </Paragraph>

              <Paragraph>
                We reserve the right to modify, suspend, or discontinue
                any aspect of the site or services at any time without notice.
              </Paragraph>

              <Divider />

              <SectionTitle>3. Registration data and privacy</SectionTitle>

              <Paragraph>
                Certain services may require you to provide information
                voluntarily. You agree that all information provided is
                accurate and kept up to date.
              </Paragraph>

              <Paragraph>
                Use of personal information is governed by our{' '}
                <EmailLink href="/privacy">Privacy Policy</EmailLink>.
              </Paragraph>

              <Divider />

              <SectionTitle>4. Conduct on site</SectionTitle>

              <Paragraph>
                You agree not to use the site to upload, distribute, or
                facilitate content that:
              </Paragraph>

              <List>
                <li>Is unlawful, misleading, defamatory, or invasive of privacy</li>
                <li>Infringes intellectual property or proprietary rights</li>
                <li>Contains malware, viruses, or malicious code</li>
                <li>Constitutes spam, solicitation, or impersonation</li>
                <li>Interferes with site security or other users</li>
              </List>

              <Paragraph>
                SME Cap Advisory reserves the right to remove content or
                restrict access at its discretion.
              </Paragraph>

              <Divider />

              <SectionTitle>5. Fees and billing</SectionTitle>

              <Paragraph>
                Fees for advisory services are governed by separate
                engagement letters or agreements where applicable.
              </Paragraph>

              <Paragraph>
                Unless otherwise agreed in writing, invoices are payable
                within the specified timeframe set out in the engagement
                documentation.
              </Paragraph>

              <Divider />

              <SectionTitle>6. Third-party sites</SectionTitle>

              <Paragraph>
                This site may contain links to third-party websites.
                SME Cap Advisory is not responsible for the content,
                accuracy, or practices of third-party sites.
              </Paragraph>

              <Divider />

              <SectionTitle>7. Intellectual property</SectionTitle>

              <Paragraph>
                All content on this site is the property of SME Cap Advisory
                or its licensors and is protected by applicable intellectual
                property laws.
              </Paragraph>

              <Paragraph>
                You may not reproduce, distribute, or exploit any content
                without prior written consent.
              </Paragraph>

              <Divider />

              <SectionTitle>8. Accuracy of information</SectionTitle>

              <Paragraph>
                Information on this site is provided for general informational
                purposes only and does not constitute financial, legal, or
                investment advice.
              </Paragraph>

              <Divider />

              <SectionTitle>9. Disclaimer of warranties</SectionTitle>

              <Paragraph>
                All services and materials are provided “as is” and
                “as available” without warranties of any kind,
                express or implied.
              </Paragraph>

              <Divider />

              <SectionTitle>10. Limitation of liability</SectionTitle>

              <Paragraph>
                To the maximum extent permitted by law, SME Cap Advisory
                shall not be liable for indirect, incidental, or consequential
                damages arising from use of the site or services.
              </Paragraph>

              <Divider />

              <SectionTitle>11. Indemnification</SectionTitle>

              <Paragraph>
                You agree to indemnify and hold harmless SME Cap Advisory
                and its affiliates from claims arising out of your use
                of the site or breach of these Terms.
              </Paragraph>

              <Divider />

              <SectionTitle>12. Governing law</SectionTitle>

              <Paragraph>
                These Terms are governed by the laws of New South Wales,
                Australia. You submit to the exclusive jurisdiction of
                its courts.
              </Paragraph>

              <Divider />

              <SectionTitle>13. Contact</SectionTitle>

              <Paragraph>
                For questions regarding these Terms, contact{' '}
                <EmailLink href="mailto:team@smecapadvisory.com">
                  team@smecapadvisory.com
                </EmailLink>
                .
              </Paragraph>
            </Card>
          </Inner>
        </Section>
      </Page>
    </ThemeProvider>
  );
};

export default TermsPage;

/* ======================================================
   STYLES (Light theme – shared with Privacy)
====================================================== */

const Page = styled.div``;

const Hero = styled.section`
  padding: 160px 24px 0;
`;

const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 13px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.subText};
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 500;
  margin: 0 0 12px;
`;

const HeroSub = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.subText};

  line-height: 1.7;
`;

const Section = styled.section`
  padding: 80px 24px 140px;
`;

const Inner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  padding: 36px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 32px 0 14px;
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: ${({ theme }) => theme.subText};
  margin: 0 0 14px;
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 12px 0 24px;
  color: ${({ theme }) => theme.subText};

  li {
    margin-bottom: 10px;
    line-height: 1.6;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.border};
  margin: 36px 0;
`;

const EmailLink = styled.a`
  color: ${({ theme }) => theme.text};
  text-decoration: none;
  border-bottom: 1px solid rgba(0,0,0,0.25);

  &:hover {
    border-color: #000;
  }
`;

/* --- Nav --- */

const Nav = styled.div`
  position: fixed;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 90;
  padding: 0 18px;
`;

const NavInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  background: rgba(255,255,255,0.85);
  backdrop-filter: blur(14px);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLeft = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.subText};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(0,0,0,0.06);
    color: #000;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 8px;
`;

const NavLink = styled.a`
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme, $active }) =>
    $active ? '#fff' : theme.subText};
  background: ${({ $active }) =>
    $active ? 'rgba(0,0,0,1)' : 'transparent'};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.border : 'transparent'};
transition: all 0.4s;

${({ $active }) => !$active && css`
    &:hover {
        background: rgba(0,0,0,0.08);
        color: #000;
    }
`}
 
`;
